import { useRef, useState } from 'react';
import { ApiResourceState } from '../types';
import { useAuthentication } from 'src/features/authentication/context';
import { ReportsService } from 'src/services/reports/reports.service';
import { useSnackbar } from 'notistack';
import { useFormContext } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router';
import { CanceledError } from 'axios';
import { REPORTS_BUILD_PREFIX, REPORTS_HISTORY } from 'src/routes/config';
type ReportState = {
  status: ApiResourceState;
  error: Error | null;
  data: any;
};

type UseReportGenerateStateArgs = {
  setDisableActions: (val: boolean) => void;
};

const useReportGenerateState = ({ setDisableActions }: UseReportGenerateStateArgs) => {
  const abortControllerRef = useRef<AbortController>(new AbortController());
  const initialState: ReportState = {
    status: 'INITIAL',
    error: null,
    data: null,
  };
  const [reportsGenerationState, setReportsGenerationState] = useState<ReportState>(initialState);
  const { getValues } = useFormContext();
  const { enqueueSnackbar } = useSnackbar();
  const queryParams = new URLSearchParams(window.location.search);
  const templateId = queryParams.get('templateId');
  const navigate = useNavigate();
  const location = useLocation();

  const {
    customerId: { value: customerId },
  } = useAuthentication();

  const handleReportsGenerate = async () => {
    const data = { ...getValues() };

    if (!data.data.timeZone) {
      return enqueueSnackbar('Timezone is required!', { variant: 'error' });
    }
    setReportsGenerationState((prevState) => ({ ...prevState, status: 'LOADING' }));
    try {
      setDisableActions(true);
      const response = await ReportsService.generateReport(customerId!!, {
        templateId: templateId!!,
        reportType: data.header.reportType.id,
      });
      const newPath = `${REPORTS_BUILD_PREFIX}/${REPORTS_HISTORY}`;
      const searchParms = location.search;

      setDisableActions(false);
      navigate(`${newPath}${searchParms}`);
      setReportsGenerationState((prevState) => ({
        ...prevState,
        data: response,
        status: 'SUCCESS',
      }));
    } catch (err) {
      abortControllerRef.current = new AbortController();
      setDisableActions(false);
      setReportsGenerationState((prevState) => ({ ...prevState, error: err, status: 'ERROR' }));
      if (err instanceof CanceledError) {
        return;
      }
      enqueueSnackbar('Could not generate report!\n' + err.message, { variant: 'error' });
    }
  };

  const handleReportCancel = () => {
    abortControllerRef.current.abort();
    setReportsGenerationState(initialState);
  };

  return { state: reportsGenerationState, handleReportsGenerate, handleReportCancel };
};

export default useReportGenerateState;
